import type { ReactNode, FormEvent } from "react";
import { Button } from "@/components/ui/Button";

// Modal — the shared overlay primitive (Phase 4c). Replaces the ~12 hand-rolled
// `fixed inset-0 z-50 ... bg-black/40` backdrops that each re-implemented the
// same backdrop + clickaway + centered panel + error line + cancel/submit
// footer. Backdrop click closes; the panel stops propagation so inner clicks
// don't.
//
// Pass `onSubmit` to render the panel as a <form> (the common edit-modal shape)
// with a built-in Cancel/Submit footer. Omit it for a plain content panel; pass
// a custom `footer` to override the default buttons entirely. The class strings
// reproduce the pre-extraction markup verbatim so migrating an existing modal is
// a behavior- and pixel-preserving change (see Modal.test.tsx).

export type ModalSize = "md" | "lg" | "xl" | "2xl";

const SIZE: Record<ModalSize, string> = {
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
};

export function Modal({
  title,
  onClose,
  children,
  size = "xl",
  error,
  onSubmit,
  submitLabel = "保存",
  submittingLabel = "处理中…",
  cancelLabel = "取消",
  submitting = false,
  submitDisabled = false,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  size?: ModalSize;
  error?: string | null;
  /** When set, the panel is a <form> and the default submit button is type=submit. */
  onSubmit?: (e: FormEvent) => void;
  submitLabel?: string;
  submittingLabel?: string;
  cancelLabel?: string;
  submitting?: boolean;
  submitDisabled?: boolean;
  /** Replaces the default Cancel/Submit footer entirely. */
  footer?: ReactNode;
}) {
  const panelClass = `w-full ${SIZE[size]} rounded-lg border border-border bg-background p-5 shadow-lg`;

  const inner = (
    <>
      <h2 className="mb-3 text-lg font-semibold">{title}</h2>
      {children}
      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        {footer ?? (
          <>
            <Button type="button" variant="outline" onClick={onClose}>
              {cancelLabel}
            </Button>
            <Button
              type={onSubmit ? "submit" : "button"}
              disabled={submitting || submitDisabled}
              onClick={onSubmit ? undefined : onClose}
            >
              {submitting ? submittingLabel : submitLabel}
            </Button>
          </>
        )}
      </div>
    </>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      {onSubmit ? (
        <form className={panelClass} onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}>
          {inner}
        </form>
      ) : (
        <div className={panelClass} onClick={(e) => e.stopPropagation()}>
          {inner}
        </div>
      )}
    </div>
  );
}
